import tinycolor from 'tinycolor2';

/**
 * Determine whether the provided CSS colour string represents a fully transparent colour.
 * Supports keywords, rgba/hsla notation, and hex values with alpha.
 *
 * @param {string} color
 * @returns {boolean}
 */
export const isTransparentColor = (color) => {
  if (!color) {
    return true;
  }

  const normalised = `${color}`.trim().toLowerCase();
  if (!normalised || normalised === 'transparent' || normalised === 'none') {
    return true;
  }

  const rgbaMatch = normalised.match(/rgba?\(([^)]+)\)/);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((part) => part.trim());
    if (parts.length === 4) {
      const alpha = parseFloat(parts[3]);
      if (!Number.isNaN(alpha)) {
        return alpha === 0;
      }
    }
  }

  const hslaMatch = normalised.match(/hsla?\(([^)]+)\)/);
  if (hslaMatch) {
    const parts = hslaMatch[1].split(',').map((part) => part.trim());
    if (parts.length === 4) {
      const alpha = parseFloat(parts[3]);
      if (!Number.isNaN(alpha)) {
        return alpha === 0;
      }
    }
  }

  if (normalised.startsWith('#')) {
    const hex = normalised.slice(1);
    if (hex.length === 8) {
      const alphaHex = hex.slice(6);
      return parseInt(alphaHex, 16) === 0;
    }
    if (hex.length === 4) {
      return parseInt(hex.slice(3), 16) === 0;
    }
  }

  return false;
};

const toHexWithAlpha = (colorInstance) => {
  if (!colorInstance || !colorInstance.isValid()) {
    return null;
  }
  return colorInstance.getAlpha() < 1 ? colorInstance.toHex8String() : colorInstance.toHexString();
};

/**
 * Generate a secondary colour derived from the base text colour.
 * Dark themes (light text) produce a darker tone, light themes (dark text) produce a lighter tone.
 *
 * @param {string} baseColor
 * @param {{ lighten?: number; darken?: number }} [options]
 * @returns {string}
 */
export const getSecondaryColorVariant = (baseColor, { lighten = 45, darken = 40 } = {}) => {
  const color = tinycolor(baseColor);
  if (!color.isValid()) {
    return baseColor;
  }

  const adjusted = color.isDark() ? color.clone().lighten(lighten) : color.clone().darken(darken);
  return toHexWithAlpha(adjusted) || baseColor;
};
